import { useEffect, useState } from "react";
import { Trash2, UserCog, ShieldCheck, Database, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfirmDialog from "../components/ConfirmDialog";
import BackupPanel from "../components/BackupPanel";
import CommittentiPanel from "../components/CommittentiPanel";
import LookupsPanel from "../components/LookupsPanel";
import OutlookCalendarCard from "../components/OutlookCalendarCard";
import NotificationSettingsPanel from "../components/NotificationSettingsPanel";
import { OPTIONS } from "../lib/entities";
import { initials } from "../lib/format";

export default function Settings() {
  const { user, can } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales" });
  const [purge, setPurge] = useState(false);
  const [delUser, setDelUser] = useState(null);

  const removeUser = async () => {
    try { await api.delete(`/auth/users/${delUser.id}`); toast.success("Utente eliminato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setDelUser(null); load();
  };

  const load = () => api.get("/auth/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const addUser = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Compila tutti i campi"); return; }
    try { await api.post("/auth/users", form); toast.success("Utente creato"); setForm({ name: "", email: "", password: "", role: "sales" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const doPurge = async () => {
    const { data } = await api.delete("/demo/purge");
    const n = Object.values(data.deleted).reduce((a, b) => a + b, 0);
    toast.success(`Dati DEMO eliminati (${n} record)`);
    setPurge(false);
    window.dispatchEvent(new Event("crm-refresh"));
  };

  const doSeed = async () => {
    const { data } = await api.post("/demo/seed");
    toast[data.seeded ? "success" : "info"](data.seeded ? "Dati DEMO ricreati" : `Nessuna azione: ${data.reason}`);
    window.dispatchEvent(new Event("crm-refresh"));
  };

  return (
    <div>
      <PageHeader title="Impostazioni" subtitle="Gestione utenti, ruoli e dati demo" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm font-heading font-semibold flex items-center gap-2 mb-4"><UserCog className="w-4 h-4 text-teal-600" /> Utenti & Ruoli</p>
          <div className="space-y-2 mb-4">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ background: u.avatar_color }}>{initials(u.name)}</span>
                <div className="flex-1"><p className="text-sm font-medium text-slate-800">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></div>
                <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">{u.role_label}</span>
                {can("admin") && u.id !== user?.id && <button data-testid={`delete-user-${u.id}`} onClick={() => setDelUser(u)} className="icon-btn p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
          {can("admin") && (
            <div className="border-t border-slate-100 pt-4 space-y-2.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Nuovo utente</p>
              <Input data-testid="new-user-name" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="new-user-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="new-user-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{OPTIONS.role.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="create-user-btn" onClick={addUser} className="w-full bg-navy-900 hover:bg-navy-700 text-white">Crea utente</Button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm font-heading font-semibold flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-teal-600" /> Il tuo profilo</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Nome</span><span className="text-slate-800">{user?.name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="text-slate-800">{user?.email}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Ruolo</span><span className="text-teal-700 font-medium">{user?.role_label}</span></div>
            </div>
          </div>

          {can("admin") && (
            <div className="bg-white rounded-xl border border-red-200 p-5">
              <p className="text-sm font-heading font-semibold flex items-center gap-2 mb-2 text-red-700"><Database className="w-4 h-4" /> Dati DEMO</p>
              <p className="text-sm text-slate-500 mb-4">Elimina in blocco tutti i record marcati come DEMO (inclusa la relativa cronologia). Utenti, pipeline e dati reali restano intatti. I dati DEMO non vengono mai ricreati automaticamente.</p>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="purge-demo-btn" onClick={() => setPurge(true)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 gap-1.5"><Trash2 className="w-4 h-4" /> Elimina dati DEMO</Button>
                <Button data-testid="seed-demo-btn" onClick={doSeed} variant="outline" className="gap-1.5">Ricrea dati DEMO</Button>
              </div>
            </div>
          )}

          {can("admin") && <NotificationSettingsPanel />}
          {can("admin") && <OutlookCalendarCard />}

          {can("admin") && <CommittentiPanel />}
          {can("admin") && <LookupsPanel />}

          {can("admin") && <BackupPanel />}

          {can("import") && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="settings-import-center-card">
              <p className="text-sm font-heading font-semibold flex items-center gap-2 mb-2"><Upload className="w-4 h-4 text-teal-600" /> Import Center & Data Sync</p>
              <p className="text-sm text-slate-500 mb-3">Importazione controllata di database esterni (CSV, TSV, XLS, XLSX, JSON) con mapping, deduplica, storico e rollback.</p>
              <Link to="/import" data-testid="settings-open-import-center" className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-navy-900 hover:bg-navy-700 rounded-lg px-3 h-9"><Upload className="w-4 h-4" /> Apri Import Center</Link>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm font-heading font-semibold mb-3">Localizzazione</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Lingua</span><span className="text-slate-800">Italiano (IT/EN predisposto)</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Formato data</span><span className="text-slate-800 font-mono">GG/MM/AAAA</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Valuta</span><span className="text-slate-800 font-mono">EUR €</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Timezone</span><span className="text-slate-800 font-mono">Europe/Rome</span></div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog open={purge} onOpenChange={setPurge} title="Eliminare tutti i dati DEMO?" description="Verranno rimossi aziende, contatti, lead, opportunità, attività, campagne e progetti marcati come DEMO." onConfirm={doPurge} />
      <ConfirmDialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)} title="Eliminare utente?" description={`Rimuovere l'accesso di ${delUser?.name} (${delUser?.email})?`} onConfirm={removeUser} />
    </div>
  );
}
